import { Alert, Box } from "@mui/material"
import {
  BASE_LAYER_LABEL,
  compareEngineVersions,
  promptLayerLabel,
  type PromptLayer
} from "@pages/main/scripts/prompts/prompt.models"

interface PromptLayerBannerProps {
  /** The layer of the row being edited. */
  layer: PromptLayer
  name: string
  /** Every engine version the AI service runs, newest last. */
  engineVersions: string[]
  /** The versions that already have their own copy of this prompt name. */
  forkedInto: string[]
}

/**
 * Who reads the row currently open in the editor, said out loud above the editor.
 *
 * The page's one real hazard is editing the right prompt on the wrong layer: the name, the text
 * and every control look identical, and the mistake is invisible until an engine answers with
 * text nobody meant it to have. So the editor states, in one sentence, which versions this row
 * reaches — and warns when it reaches fewer than the admin probably expects.
 */
export const PromptLayerBanner = ({
  layer,
  name,
  engineVersions,
  forkedInto
}: PromptLayerBannerProps) => {
  const readsBase = engineVersions
    .filter((version) => !forkedInto.includes(version))
    .sort(compareEngineVersions)

  const shadowed = layer
    ? []
    : forkedInto
        .filter((version) => version !== layer)
        .sort(compareEngineVersions)

  const severity = layer ? "info" : shadowed.length ? "warning" : "success"

  return (
    <Alert
      severity={severity}
      icon={false}
      sx={{
        py: 0,
        px: 1.25,
        fontSize: "0.75rem",
        "& .MuiAlert-message": { py: 0.5, width: "100%" }
      }}
    >
      <Box component="strong" sx={{ mr: 0.75 }}>
        {layer ? `${promptLayerLabel(layer)} only` : BASE_LAYER_LABEL}
      </Box>
      {layer ? (
        <>
          Editing this changes <strong>{layer}</strong> alone.{" "}
          {readsBase.length
            ? `${readsBase.join(", ")} keep reading the ${BASE_LAYER_LABEL} row.`
            : `Nothing else reads the ${BASE_LAYER_LABEL} row of this prompt.`}
        </>
      ) : shadowed.length ? (
        <>
          Read by{" "}
          {readsBase.length ? readsBase.join(", ") : "no running version"}.{" "}
          <strong>{shadowed.join(", ")}</strong> has its own copy of {name} and
          will <strong>not</strong> see this edit — open that layer to change it
          too.
        </>
      ) : (
        <>
          Read by{" "}
          {readsBase.length ? readsBase.join(", ") : "every engine version"}. No
          version has forked {name}, so this edit reaches all of them.
        </>
      )}
    </Alert>
  )
}
